// [2026-03-21] 사용자 관리 페이지 — 슈퍼관리자 전용
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersAPI, tenantsAPI } from '../services/api';

const getErrorMessage = (error: any): string => {
  return error?.response?.data?.detail || '요청 처리 중 오류가 발생했습니다.';
};

export function UsersPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [detailUser, setDetailUser] = useState<any>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [errorAlert, setErrorAlert] = useState<string | null>(null);
  const [successAlert, setSuccessAlert] = useState<string | null>(null);
  const [createForm, setCreateForm] = useState({ email: '', password: '', name: '', tenant_id: '' });
  const [showNewTenant, setShowNewTenant] = useState(false);
  const [newTenantForm, setNewTenantForm] = useState({ name: '', slug: '' });
  const [assignForm, setAssignForm] = useState({ tenant_id: '', role: 'viewer' });

  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users', page, search],
    queryFn: () => usersAPI.list(page, 20, search || undefined),
  });

  const { data: tenantsData } = useQuery({
    queryKey: ['tenants-all'],
    queryFn: () => tenantsAPI.list(1, 100),
  });

  const users = usersData?.data?.items || [];
  const totalPages = usersData?.data?.pages || 1;
  const tenants = tenantsData?.data?.items || [];

  // [2026-03-22] 사용자 생성 — 새 테넌트도 함께 생성 가능
  const createMutation = useMutation({
    mutationFn: async () => {
      let tenantId = createForm.tenant_id || null;

      // 새 테넌트 생성
      if (showNewTenant && newTenantForm.name) {
        const tenantRes = await tenantsAPI.create({
          name: newTenantForm.name,
          slug: newTenantForm.slug || newTenantForm.name.toLowerCase().replace(/\s+/g, '-'),
          allowed_domains: ['*'],
        });
        tenantId = tenantRes.data.id;
      }

      return usersAPI.create({
        email: createForm.email,
        password: createForm.password,
        name: createForm.name || null,
        tenant_id: tenantId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['my-tenants'] });
      setShowCreateModal(false);
      setCreateForm({ email: '', password: '', name: '', tenant_id: '' });
      setShowNewTenant(false);
      setNewTenantForm({ name: '', slug: '' });
      setSuccessAlert('사용자가 생성되었습니다.');
    },
    onError: (error: any) => setErrorAlert(getErrorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => usersAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowDetailPanel(false);
      setDetailUser(null);
    },
    onError: (error: any) => setErrorAlert(getErrorMessage(error)),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      usersAPI.update(id, { is_active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      if (detailUser) refreshDetail(detailUser.id);
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (id: string) => usersAPI.resetPassword(id),
    onSuccess: (res) => {
      setSuccessAlert(`임시 비밀번호: ${res.data.temp_password}`);
    },
    onError: (error: any) => setErrorAlert(getErrorMessage(error)),
  });

  const assignTenantMutation = useMutation({
    mutationFn: () => usersAPI.assignTenant(detailUser.id, assignForm.tenant_id, assignForm.role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowAssignModal(false);
      setAssignForm({ tenant_id: '', role: 'viewer' });
      refreshDetail(detailUser.id);
    },
    onError: (error: any) => setErrorAlert(getErrorMessage(error)),
  });

  const removeTenantMutation = useMutation({
    mutationFn: (tenantId: string) => usersAPI.removeTenant(detailUser.id, tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      refreshDetail(detailUser.id);
    },
    onError: (error: any) => setErrorAlert(getErrorMessage(error)),
  });

  const refreshDetail = async (userId: string) => {
    try {
      const res = await usersAPI.get(userId);
      setDetailUser(res.data);
    } catch {}
  };

  const openDetail = async (user: any) => {
    try {
      const res = await usersAPI.get(user.id);
      setDetailUser(res.data);
    } catch {
      setDetailUser(user);
    }
    setShowDetailPanel(true);
  };

  return (
    <div className="p-8">
      {/* 알림 */}
      {errorAlert && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex justify-between items-center">
          <span className="text-red-700">{errorAlert}</span>
          <button onClick={() => setErrorAlert(null)} className="text-red-400 hover:text-red-600">&times;</button>
        </div>
      )}
      {successAlert && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex justify-between items-center">
          <span className="text-green-700">{successAlert}</span>
          <button onClick={() => setSuccessAlert(null)} className="text-green-400 hover:text-green-600">&times;</button>
        </div>
      )}

      {/* 헤더 */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">사용자 관리</h1>
          <p className="text-gray-600">사용자 계정을 관리하고 테넌트를 할당합니다.</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          + 사용자 추가
        </button>
      </div>

      {/* 검색 */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="이메일 또는 이름으로 검색..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* 테이블 */}
      {isLoading ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">로딩 중...</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">이메일</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">이름</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">상태</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">테넌트</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">마지막 로그인</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">작업</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map((u: any) => (
                <tr key={u.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openDetail(u)}>
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{u.email}</div>
                    {u.is_superuser && (
                      <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded">슈퍼관리자</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-gray-600">{u.name || '-'}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {u.is_active ? '활성' : '비활성'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {u.tenants && u.tenants.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {u.tenants.map((t: any) => (
                          <span key={t.tenant_id} className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                            {t.tenant_name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-gray-500 text-sm">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString('ko-KR') : '-'}
                  </td>
                  <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => toggleActiveMutation.mutate({ id: u.id, is_active: !u.is_active })}
                      className={`text-sm px-3 py-1 rounded ${
                        u.is_active ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'
                      }`}
                    >
                      {u.is_active ? '비활성화' : '활성화'}
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-400">등록된 사용자가 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 p-4 border-t">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="px-3 py-1 border rounded disabled:opacity-50">이전</button>
              <span className="text-sm text-gray-600">{page} 페이지</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50">다음</button>
            </div>
          )}
        </div>
      )}

      {/* 사용자 생성 모달 */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-30" onClick={() => setShowCreateModal(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold mb-4">사용자 추가</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">이메일 *</label>
                <input type="email" value={createForm.email}
                  onChange={(e) => setCreateForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">비밀번호 *</label>
                <input type="password" value={createForm.password}
                  onChange={(e) => setCreateForm(f => ({ ...f, password: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">이름</label>
                <input type="text" value={createForm.name}
                  onChange={(e) => setCreateForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">기본 테넌트</label>
                {!showNewTenant ? (
                  <>
                    <select value={createForm.tenant_id}
                      onChange={(e) => setCreateForm(f => ({ ...f, tenant_id: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg">
                      <option value="">선택 안 함</option>
                      {tenants.map((t: any) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                    <button type="button" onClick={() => setShowNewTenant(true)}
                      className="mt-1 text-sm text-blue-600 hover:text-blue-800">
                      + 새 테넌트 만들기
                    </button>
                  </>
                ) : (
                  <div className="border rounded-lg p-3 bg-blue-50 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-blue-700">새 테넌트</span>
                      <button type="button" onClick={() => setShowNewTenant(false)}
                        className="text-xs text-gray-500 hover:text-gray-700">기존 테넌트 선택</button>
                    </div>
                    <input type="text" placeholder="테넌트명"
                      value={newTenantForm.name}
                      onChange={(e) => setNewTenantForm(f => ({ ...f, name: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg text-sm" />
                    <input type="text" placeholder="슬러그 (영문, 자동 생성 가능)"
                      value={newTenantForm.slug}
                      onChange={(e) => setNewTenantForm(f => ({ ...f, slug: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg text-sm" />
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50">취소</button>
              <button onClick={() => createMutation.mutate()}
                disabled={!createForm.email || !createForm.password || createMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {createMutation.isPending ? '생성 중...' : '생성'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 사용자 상세 패널 */}
      {showDetailPanel && detailUser && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black bg-opacity-30"
            onClick={() => { setShowDetailPanel(false); setDetailUser(null); }} />
          <div className="relative w-full max-w-lg bg-white shadow-2xl overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-lg font-semibold">{detailUser.email}</h2>
              <button onClick={() => { setShowDetailPanel(false); setDetailUser(null); }}
                className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <div className="p-6 space-y-6">
              {/* 기본 정보 */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-3">기본 정보</h3>
                <dl className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="text-gray-400">이름</dt>
                    <dd className="font-medium">{detailUser.name || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-400">상태</dt>
                    <dd>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        detailUser.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {detailUser.is_active ? '활성' : '비활성'}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-400">생성일</dt>
                    <dd>{new Date(detailUser.created_at).toLocaleString('ko-KR')}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-400">마지막 로그인</dt>
                    <dd>{detailUser.last_login_at ? new Date(detailUser.last_login_at).toLocaleString('ko-KR') : '-'}</dd>
                  </div>
                </dl>
              </div>

              {/* 소속 테넌트 */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-sm font-medium text-gray-500">소속 테넌트</h3>
                  <button onClick={() => setShowAssignModal(true)}
                    className="text-sm text-blue-600 hover:text-blue-800">+ 추가</button>
                </div>
                {detailUser.tenants && detailUser.tenants.length > 0 ? (
                  <div className="space-y-2">
                    {detailUser.tenants.map((t: any) => (
                      <div key={t.tenant_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <span className="font-medium text-gray-900">{t.tenant_name}</span>
                          <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                            t.role === 'admin' ? 'bg-orange-100 text-orange-700' : 'bg-gray-200 text-gray-600'
                          }`}>
                            {t.role}
                          </span>
                        </div>
                        <button onClick={() => {
                          if (confirm(`${t.tenant_name} 할당을 해제하시겠습니까?`))
                            removeTenantMutation.mutate(t.tenant_id);
                        }} className="text-red-500 hover:text-red-700 text-sm">해제</button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">할당된 테넌트가 없습니다.</p>
                )}
              </div>

              {/* 액션 */}
              <div className="flex gap-2 pt-2 border-t">
                <button onClick={() => resetPasswordMutation.mutate(detailUser.id)}
                  disabled={resetPasswordMutation.isPending}
                  className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 text-sm">
                  비밀번호 초기화
                </button>
                {!detailUser.is_superuser && (
                  <button onClick={() => {
                    if (confirm('사용자를 삭제하시겠습니까?')) deleteMutation.mutate(detailUser.id);
                  }} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm">
                    삭제
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 테넌트 할당 모달 */}
      {showAssignModal && detailUser && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-30" onClick={() => setShowAssignModal(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
            <h2 className="text-lg font-semibold mb-4">테넌트 할당</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">테넌트</label>
                <select value={assignForm.tenant_id}
                  onChange={(e) => setAssignForm(f => ({ ...f, tenant_id: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg">
                  <option value="">선택하세요</option>
                  {tenants
                    .filter((t: any) => !detailUser.tenants?.some((ut: any) => ut.tenant_id === t.id))
                    .map((t: any) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">역할</label>
                <select value={assignForm.role}
                  onChange={(e) => setAssignForm(f => ({ ...f, role: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg">
                  <option value="admin">admin (관리자)</option>
                  <option value="viewer">viewer (조회자)</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowAssignModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50">취소</button>
              <button onClick={() => assignTenantMutation.mutate()}
                disabled={!assignForm.tenant_id || assignTenantMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                할당
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
